import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Typography, FormControlLabel, Checkbox,
  Box, LinearProgress
} from '@mui/material';
import { booksAPI } from '../../services/api';
import { Link } from 'react-router-dom';

const TEXT_TYPES = {
  'original': 'Original',
  'short': 'Short Version',
  'medium': 'Medium Version',
  'long': 'Long Version',
  'block': 'Course Block',
  'bulk': 'Multiple Blocks'
};

// OpenAI standard voices
const OPENAI_VOICES = [
  { value: 'alloy', label: 'Alloy' },
  { value: 'ash', label: 'Ash' },
  { value: 'ballad', label: 'Ballad' },
  { value: 'coral', label: 'Coral' },
  { value: 'echo', label: 'Echo' },
  { value: 'fable', label: 'Fable' },
  { value: 'nova', label: 'Nova' },
  { value: 'onyx', label: 'Onyx' },
  { value: 'sage', label: 'Sage' },
  { value: 'shimmer', label: 'Shimmer' }
];

const AudioGenerationDialog = ({ 
  open, 
  onClose, 
  selectedText, 
  onSuccess, 
  onError,
  bulkProgress // Add this new prop
}) => {
  const [loading, setLoading] = useState(false);
  const [fetchingVoices, setFetchingVoices] = useState(false);
  const [voicesError, setVoicesError] = useState(null);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [audioForm, setAudioForm] = useState({
    voice: 'nova',
    model: 'tts-1-hd',
    endpoint: 'longform', // Changed default to longform
    voiceName: '', // Will be populated dynamically
    languageCode: 'en-US', // Google language code
    isSsml: false, // Add SSML flag
    instructions: '' // For OpenAI long-form specific instructions
  });

  // Fetch available voices when the dialog opens or language changes
  useEffect(() => {
    if (!open || audioForm.endpoint === 'openai' || audioForm.endpoint === 'openai-longform') return;
    
    const fetchVoices = async () => {
      try {
        setFetchingVoices(true);
        setVoicesError(null);
        
        const response = await booksAPI.getAvailableTTSVoices(audioForm.languageCode);
        
        if (response.data && Array.isArray(response.data.voices)) {
          setAvailableVoices(response.data.voices);
          
          // Set first voice as default if available and no voice is selected
          if (response.data.voices.length > 0 && !audioForm.voiceName) {
            setAudioForm(prev => ({
              ...prev,
              voiceName: response.data.voices[0].name
            }));
          }
        } else {
          setAvailableVoices([]);
          setVoicesError('No voices returned from API');
        }
      } catch (error) {
        console.error('Error fetching voices:', error);
        setVoicesError('Failed to fetch available voices');
        setAvailableVoices([]);
      } finally {
        setFetchingVoices(false);
      }
    };
    
    fetchVoices();
  }, [open, audioForm.endpoint, audioForm.languageCode, audioForm.voiceName]);

  // Load voices on initial dialog open for longform (since it's now the default)
  useEffect(() => {
    if (open && audioForm.endpoint !== 'openai' && audioForm.endpoint !== 'openai-longform' && !audioForm.voiceName) {
      const fetchInitialVoices = async () => {
        try {
          setFetchingVoices(true);
          const response = await booksAPI.getAvailableTTSVoices(audioForm.languageCode);
          
          if (response.data && Array.isArray(response.data.voices) && response.data.voices.length > 0) {
            setAvailableVoices(response.data.voices);
            setAudioForm(prev => ({
              ...prev,
              voiceName: response.data.voices[0].name
            }));
          }
        } catch (error) {
          console.error('Error fetching initial voices:', error);
        } finally {
          setFetchingVoices(false);
        }
      };
      
      fetchInitialVoices();
    }
  }, [open]);

  const handleAudioFormChange = (e) => {
    const { name, value, checked } = e.target;
    
    // Special handling when changing endpoint
    if (name === 'endpoint') {
      // Reset voice selection when changing endpoints
      if (value === 'openai' || value === 'openai-longform') {
        setAudioForm({
          ...audioForm,
          [name]: value,
          voice: 'nova' // Default OpenAI voice
        });
      } else {
        // For Google or Longform, reset the voiceName
        setAudioForm({
          ...audioForm,
          [name]: value,
          voiceName: '' // Will be populated after fetching
        });
      }
    } else {
      // Default handling for other form fields
      setAudioForm({
        ...audioForm,
        [name]: name === 'isSsml' ? checked : value
      });
    }
  };

  const handleGenerateAudio = async () => {
    if (!selectedText) return;

    try {
      setLoading(true);
      
      // Check if we're dealing with a course block or book text
      if (selectedText.type === 'block' || selectedText.type === 'bulk') {
        // For course blocks or bulk, pass the correct form data structure to the parent component
        if (onSuccess) {
          onSuccess(audioForm);
        }
      } else {
        // For book texts, use the original direct API call behavior
        let response;
        
        // Call appropriate endpoint based on selection
        switch (audioForm.endpoint) {
          case 'longform':
            response = await booksAPI.generateLongformAudio({
              bookTextId: selectedText.id,
              voiceName: audioForm.voiceName,
              languageCode: audioForm.languageCode,
              useSsml: audioForm.isSsml
              // Don't pass audioEncoding to avoid API validation errors
            });
            break;
          case 'openai-longform':
          default:
            response = await booksAPI.generateLongFormOpenAIAudio({
              bookTextId: selectedText.id,
              voice: audioForm.voice,
              model: audioForm.model,
              instructions: audioForm.instructions || undefined
            });
            break;
        }

        if (onSuccess) {
          onSuccess(response);
        }
      }
    } catch (error) {
      console.error('Error generating audio:', error);
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
      onClose();
    }
  };

  // Generate voice list items based on current endpoint
  const renderVoiceOptions = () => {
    if (audioForm.endpoint === 'openai' || audioForm.endpoint === 'openai-longform') {
      return OPENAI_VOICES.map(voice => (
        <MenuItem key={voice.value} value={voice.value}>
          {voice.label}
        </MenuItem>
      ));
    } else {
      // For Google or Longform, use dynamic voice list
      if (fetchingVoices) {
        return [
          <MenuItem key="loading" value="" disabled>
            Loading voices...
          </MenuItem>
        ];
      }
      
      if (voicesError || availableVoices.length === 0) {
        return [
          <MenuItem key="none" value="" disabled>
            No voices available
          </MenuItem>
        ];
      }
      
      return availableVoices.map(voice => (
        <MenuItem key={voice.name} value={voice.name}>
          {voice.displayName || voice.name}
        </MenuItem>
      ));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {selectedText?.type === 'bulk' ? 'Generate Audio for All Blocks' : 'Generate Audio'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          {selectedText?.type === 'block' 
            ? `Generate audio for course block: "${selectedText.title}"`
            : selectedText?.type === 'bulk'
            ? selectedText.content
            : `Generate audio for ${TEXT_TYPES[selectedText?.type || 'original']} version, id ${selectedText?.id}`}
        </Typography>
        
        {/* Show progress bar for bulk generation */}
        {bulkProgress && (
          <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={(bulkProgress.current / bulkProgress.total) * 100} 
            />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              Processing {bulkProgress.current} of {bulkProgress.total} blocks
            </Typography>
          </Box>
        )}
        
        <Link to="https://cloud.google.com/text-to-speech/docs/chirp3-hd">
          Read about Google Cloud TTS
        </Link> &nbsp;|&nbsp;
        <Link to="https://www.openai.fm/">
          Play with OpenAI TTS
        </Link>
        <FormControl fullWidth margin="normal">
          <InputLabel id="endpoint-label">Audio Provider</InputLabel>
          <Select
            labelId="endpoint-label"
            name="endpoint"
            value={audioForm.endpoint}
            onChange={handleAudioFormChange}
            label="Audio Provider"
          >
            <MenuItem value="longform">High-Quality Longform</MenuItem>
            <MenuItem value="openai-longform">OpenAI Longform TTS</MenuItem>
          </Select>
        </FormControl>
        
        {(audioForm.endpoint === 'openai' || audioForm.endpoint === 'openai-longform') && (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel id="voice-label">Voice</InputLabel>
              <Select
                labelId="voice-label"
                name="voice"
                value={audioForm.voice}
                onChange={handleAudioFormChange}
                label="Voice"
              >
                {renderVoiceOptions()}
              </Select>
            </FormControl>
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="model-label">Model</InputLabel>
              <Select
                labelId="model-label"
                name="model"
                value={audioForm.model}
                onChange={handleAudioFormChange}
                label="Model"
              >
                <MenuItem value="tts-1">TTS-1 (Standard)</MenuItem>
                <MenuItem value="tts-1-hd">TTS-1-HD (High Quality)</MenuItem>
              </Select>
            </FormControl>
            
            {audioForm.endpoint === 'openai-longform' && (
              <FormControl fullWidth margin="normal">
                <InputLabel id="instructions-label">Voice Instructions (Optional)</InputLabel>
                <Select
                  labelId="instructions-label"
                  name="instructions"
                  value={audioForm.instructions}
                  onChange={handleAudioFormChange}
                  label="Voice Instructions (Optional)"
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Use Markdown formatting as narration cues (headers for pauses, emphasis for important points)">Use Markdown formatting as narration cues (headers for pauses, emphasis for important points)</MenuItem>
                  <MenuItem value="Read at a moderate pace with clear articulation">Read at a moderate pace with clear articulation</MenuItem>
                  <MenuItem value="Use a contemplative tone suitable for non-fiction">Use a contemplative tone suitable for non-fiction</MenuItem>
                  <MenuItem value="Add subtle emphasis on key concepts and ideas">Add subtle emphasis on key concepts and ideas</MenuItem>
                  <MenuItem value="Create a professional audiobook experience">Create a professional audiobook experience</MenuItem>
                  <MenuItem value="Balance pacing with appropriate pauses for comprehension">Balance pacing with appropriate pauses for comprehension</MenuItem>
                  <MenuItem value="Use a conversational tone that connects with listeners">Use a conversational tone that connects with listeners</MenuItem>
                </Select>
              </FormControl>
            )}
          </>
        )}
        
        {(audioForm.endpoint === 'google' || audioForm.endpoint === 'longform') && (
          <>
            <FormControl fullWidth margin="normal">
              <InputLabel id="language-code-label">Language</InputLabel>
              <Select
                labelId="language-code-label"
                name="languageCode"
                value={audioForm.languageCode}
                onChange={handleAudioFormChange}
                label="Language"
              >
                <MenuItem value="en-US">English (US)</MenuItem>
                <MenuItem value="en-GB">English (UK)</MenuItem>
                <MenuItem value="ru-RU">Russian</MenuItem>
                <MenuItem value="es-ES">Spanish</MenuItem>
                <MenuItem value="fr-FR">French</MenuItem>
                <MenuItem value="de-DE">German</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="voice-name-label">Voice</InputLabel>
              <Select
                labelId="voice-name-label"
                name="voiceName"
                value={audioForm.voiceName}
                onChange={handleAudioFormChange}
                label="Voice"
                disabled={fetchingVoices || availableVoices.length === 0}
                startAdornment={fetchingVoices && (
                  <CircularProgress size={20} style={{ marginRight: 8 }} />
                )}
              >
                {renderVoiceOptions()}
              </Select>
            </FormControl>
            
            {voicesError && (
              <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
                {voicesError}. Using default voices instead.
              </Alert>
            )}
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="audio-encoding-label">Audio Format</InputLabel>
              <Select
                labelId="audio-encoding-label"
                name="audioEncoding"
                value={audioForm.audioEncoding}
                onChange={handleAudioFormChange}
                label="Audio Format"
              >
                <MenuItem value="MP3">MP3</MenuItem>
                <MenuItem value="LINEAR16">WAV (LINEAR16)</MenuItem>
                <MenuItem value="OGG_OPUS">OGG Opus</MenuItem>
              </Select>
            </FormControl>
          </>
        )}
        
        {/* Add SSML flag checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              name="isSsml"
              checked={audioForm.isSsml}
              onChange={handleAudioFormChange}
            />
          }
          label="Text contains SSML markup"
          sx={{ mt: 2, display: 'block' }}
        />
        
        {audioForm.isSsml && (
          <Typography variant="caption" color="text.secondary">
            When enabled, the text will be processed as SSML markup, allowing for detailed control of speech synthesis.
          </Typography>
        )}
        
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleGenerateAudio} 
          variant="contained" 
          disabled={loading || (audioForm.endpoint !== 'openai-longform' && !audioForm.voiceName)}
          startIcon={loading && <CircularProgress size={16} />}
        >
          {loading ? 'Generating...' : 'Generate Audio'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AudioGenerationDialog;
